/**
 * WelcomeGate
 * -----------
 * Heavily-branded welcome + HIPAA acknowledgment shown after login. The user
 * must tick "I agree" to enter. Every acknowledgment is recorded server-side
 * (who + role + notice version + when) as HIPAA documentation. Shows once per
 * day per user (survives reloads; re-appears the next calendar day).
 *
 * Layout: a wide two-column card (brand + signature on the left, HIPAA notice +
 * agreement + enter on the right) so it reads square on desktop instead of a
 * tall narrow column. Stacks to a single column on small screens.
 */
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { welcomeAPI } from "../../services/api";
import { Button } from "../ui/button";
import { ShieldCheck, Lock, EyeOff, MessageSquareLock, AlertTriangle, Check } from "lucide-react";

const NOTICE_VERSION = "2026-06";
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const HIPAA_POINTS = [
  { Icon: EyeOff, text: "Protected Health Information (PHI) is strictly confidential — access only what you need to do your job." },
  { Icon: Lock, text: "Never share your login, and always lock or sign off when you step away from your screen." },
  { Icon: MessageSquareLock, text: "Discuss client information only on secure channels and only with authorized team members." },
  { Icon: AlertTriangle, text: "Report any suspected privacy breach or security incident immediately." },
];

export default function WelcomeGate() {
  const { user, isAuthenticated } = useAuth();
  // Once per day, per user (survives reloads; re-appears the next calendar day).
  const dayKey = user ? `avida_welcome_ack_${user.id || user.email}` : "";
  const [acked, setAcked] = useState(() => {
    try { return !!dayKey && localStorage.getItem(dayKey) === todayStr(); } catch { return false; }
  });
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated || !user || acked) return null;

  const fullName = [user.first_name, user.middle_name || user.middle_initial, user.last_name]
    .filter(Boolean).join(" ").replace(/\s+/g, " ").trim() || user.email || "there";

  const handleEnter = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try { await welcomeAPI.acknowledge(NOTICE_VERSION, "web"); } catch { /* audit best-effort */ }
    try { if (dayKey) localStorage.setItem(dayKey, todayStr()); } catch { /* private mode */ }
    setAcked(true);
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: "rgba(0,20,52,0.94)", backdropFilter: "blur(6px)" }}
      role="dialog"
      aria-modal="true"
      data-testid="welcome-gate"
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl border border-slate-200 animate-in zoom-in-95 fade-in duration-300 my-6"
        style={{ background: "#FFFFFF" }}
      >
        {/* top accent bar spanning the whole card */}
        <div
          className="absolute top-0 left-0 right-0 h-1.5 z-10"
          style={{ background: "linear-gradient(90deg, #FFDA00 0%, #0866FF 55%, #0044A8 100%)" }}
          aria-hidden="true"
        />

        <div className="grid md:grid-cols-2">
          {/* LEFT — brand + welcome + signature */}
          <div
            className="relative px-8 pt-9 pb-7 flex flex-col text-center md:text-left"
            style={{ background: "radial-gradient(120% 120% at 30% 0%, #EEF4FF 0%, #FFFFFF 78%)" }}
          >
            <img
              src="/avida-logo.png"
              alt="Avida Personal Care"
              className="w-24 h-auto drop-shadow-sm mx-auto md:mx-0"
              data-testid="welcome-gate-logo"
            />
            <h1 className="mt-4 text-2xl font-black tracking-tight" style={{ color: "#0A2A6B" }}>
              Welcome to Avida Health
            </h1>
            <p className="mt-1.5 text-[15px] text-slate-600">
              {greeting()},{" "}
              <span className="font-bold" style={{ color: "#0866FF" }} data-testid="welcome-gate-name">{fullName}</span>.
            </p>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              Thank you for the care and dedication you bring to our clients every day. Before you begin,
              please take a moment to review our shared commitment to protecting the people we serve.
            </p>

            {/* Signature — pinned to the bottom so both columns balance */}
            <div className="mt-auto pt-6">
              <p className="text-[13px] text-slate-500">With gratitude,</p>
              <p
                className="text-xl text-[#0A2A6B] mt-0.5"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", fontWeight: 600 }}
              >
                Dawn Pérez Maldonado
              </p>
              <div className="mt-1.5 h-0.5 w-16 rounded-full mx-auto md:mx-0" style={{ background: "#FFDA00" }} />
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0866FF] mt-1.5">
                Owner &amp; Administrator, Avida Health
              </p>
            </div>
          </div>

          {/* RIGHT — HIPAA notice + agreement + enter */}
          <div className="px-8 py-8 flex flex-col justify-center bg-white border-t md:border-t-0 md:border-l border-slate-200">
            <div className="rounded-2xl border border-slate-200 bg-[#F6F8FB] overflow-hidden" data-testid="welcome-gate-hipaa">
              <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: "linear-gradient(90deg, #0044A8, #0866FF)" }}>
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-[#FFDA00]" />
                </div>
                <h2 className="text-sm font-black tracking-tight text-white">A quick HIPAA reminder</h2>
              </div>
              <ul className="p-4 space-y-2.5">
                {HIPAA_POINTS.map(({ Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-[#0866FF]" strokeWidth={1.9} />
                    </div>
                    <span className="text-[12.5px] text-slate-700 leading-snug">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <label
              className="flex items-start gap-3 mt-5 p-3.5 rounded-xl bg-white border border-slate-200 cursor-pointer hover:border-[#032866]/40 transition-colors"
              data-testid="welcome-gate-agree-label"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#0044A8]"
                data-testid="welcome-gate-checkbox"
              />
              <span className="text-[12.5px] text-slate-700 leading-snug">
                I acknowledge this notice and agree to handle all Protected Health Information in
                accordance with <span className="font-semibold">HIPAA</span> and Avida Health policy.
              </span>
            </label>

            <Button
              onClick={handleEnter}
              disabled={!checked || submitting}
              className="mt-4 w-full h-12 rounded-xl text-sm font-bold gap-2 disabled:opacity-50"
              style={{ backgroundColor: "#0044A8", color: "#FFDA00" }}
              data-testid="welcome-gate-continue"
            >
              <Check className="w-4 h-4" />
              {submitting ? "One moment…" : "Enter Avida Health"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
